/**
 * La palette de création — atteignable de partout.
 *
 * Le plan « Le coût en clics » : toute création coûtait deux clics, dont un de
 * navigation — aller sur la liste, puis « Nouveau ». Sur téléphone, un de
 * plus : ouvrir le menu. C'est le geste le plus répété d'une semaine.
 *
 * Trois portes, une seule pièce :
 *
 *   · **Ctrl K** (⌘ K sur Mac), de n'importe quel écran ;
 *   · le bouton **Créer** en tête du menu, pour qui ne connaît pas le
 *     raccourci — ce qui ne se voit pas ne s'utilise pas ;
 *   · le **bouton flottant** du téléphone.
 *
 * On y tape pour filtrer, les flèches choisissent, Entrée agit. Les
 * destinations du menu y sont aussi : taper « stock » y mène, sans chercher
 * dans quel groupe il est rangé.
 *
 * Deux gestes ne passent pas par une navigation, et c'est voulu :
 *
 *   · **la photo d'un ticket** ouvre le sélecteur de fichier sur le clic
 *     lui-même — un navigateur refuse de l'ouvrir après un changement de page —
 *     puis confie la photo à l'écran Dépenses, qui la lit ;
 *   · **le rendez-vous** s'ouvre sur place : il se note le combiné à
 *     l'épaule, sans quitter ce qu'on regardait.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CalendarPlus,
  Camera,
  FilePlus2,
  HardHat,
  Search,
  UserPlus,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { AppointmentDialog } from "@/components/dashboard/AgendaDuJour";
import { Dialog, DialogCloseButton } from "@/components/shared/Dialog";
import { allDestinations, matchesQuery } from "@/lib/navigation";
import { useUiStore } from "@/stores/uiStore";
import { cn } from "@/lib/utils";

interface Entree {
  id: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  kind: "creer" | "aller";
  run: () => void;
}

export function CreatePalette() {
  const open = useUiStore((s) => s.paletteOpen);
  const setOpen = useUiStore((s) => s.setPaletteOpen);
  const setPendingReceipt = useUiStore((s) => s.setPendingReceipt);
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const [rdv, setRdv] = useState(false);
  const ticketRef = useRef<HTMLInputElement>(null);
  const champ = useRef<HTMLInputElement>(null);

  // Le raccourci, écouté partout. Ctrl et ⌘ tous deux : un patron sur Mac et
  // une assistante sur PC n'ont pas à connaître deux combinaisons.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(!useUiStore.getState().paletteOpen);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setIndex(0);
      // Après le rendu : le champ n'existe pas encore au moment où l'état change.
      window.setTimeout(() => champ.current?.focus(), 0);
    }
  }, [open]);

  const fermer = () => setOpen(false);
  const aller = (to: string) => {
    fermer();
    navigate(to);
  };

  const entrees = useMemo<Entree[]>(() => {
    const creer: Entree[] = [
      { id: "devis", label: "Nouveau devis", hint: "Vendre", icon: FilePlus2, kind: "creer", run: () => aller("/devis/new") },
      {
        id: "ticket",
        label: "Photo d'un ticket",
        hint: "Dépense lue depuis la photo",
        icon: Camera,
        kind: "creer",
        // Le sélecteur s'ouvre sur ce clic : c'est la condition pour que le
        // navigateur l'autorise.
        run: () => ticketRef.current?.click(),
      },
      { id: "depense", label: "Nouvelle dépense", hint: "Acheter", icon: Wallet, kind: "creer", run: () => aller("/depenses?nouvelle=1") },
      { id: "client", label: "Nouveau client", hint: "Vendre", icon: UserPlus, kind: "creer", run: () => aller("/clients/new") },
      { id: "chantier", label: "Nouveau chantier", hint: "Produire", icon: HardHat, kind: "creer", run: () => aller("/chantiers/new") },
      {
        id: "rdv",
        label: "Nouveau rendez-vous",
        hint: "Agenda du jour",
        icon: CalendarPlus,
        kind: "creer",
        run: () => {
          fermer();
          setRdv(true);
        },
      },
    ];
    const destinations: Entree[] = allDestinations().map((d) => ({
      id: `aller:${d.to}`,
      label: d.label,
      hint: d.group,
      icon: ArrowRight,
      kind: "aller",
      run: () => aller(d.to),
    }));
    return [...creer, ...destinations];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sans saisie, seules les créations : la palette s'ouvre sur ce qu'on vient
  // y faire. Les destinations arrivent dès qu'on tape.
  const visibles = query.trim()
    ? entrees.filter((e) => matchesQuery(`${e.label} ${e.hint}`, query))
    : entrees.filter((e) => e.kind === "creer");
  const courant = Math.min(index, Math.max(0, visibles.length - 1));

  return (
    <>
      {/* Hors de la fenêtre : il doit exister même palette fermée, le temps
          que le fichier choisi revienne. */}
      <input
        ref={ticketRef}
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const photo = e.target.files?.[0] ?? null;
          e.target.value = "";
          if (!photo) return;
          setPendingReceipt(photo);
          aller("/depenses");
        }}
      />

      {rdv && <AppointmentDialog appointment={null} onClose={() => setRdv(false)} />}

      {open && (
        <Dialog
          onClose={fermer}
          label="Créer ou aller à"
          placement="top"
          zIndexClassName="z-[60]"
          className="sm:max-w-lg max-h-[80vh] flex flex-col p-0"
        >
            <div className="flex items-center gap-2 px-4 border-b border-border">
              <Search size={16} className="text-text-muted shrink-0" />
              <input
                ref={champ}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setIndex(0);
                }}
                onKeyDown={(e) => {
                  // Échap est tenu par <Dialog>.
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setIndex((i) => Math.min(i + 1, visibles.length - 1));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setIndex((i) => Math.max(i - 1, 0));
                  } else if (e.key === "Enter") {
                    e.preventDefault();
                    visibles[courant]?.run();
                  }
                }}
                placeholder="Créer un devis, aller au stock…"
                className="flex-1 h-12 bg-transparent text-sm text-text-primary placeholder:text-text-muted outline-none"
              />
              <DialogCloseButton className="sm:hidden" />
              <kbd className="hidden sm:inline font-mono text-[10.5px] text-text-muted border border-border px-1.5 py-0.5">
                Échap
              </kbd>
            </div>

            <ul className="overflow-y-auto py-1" role="listbox">
              {visibles.length === 0 ? (
                <li className="px-4 py-6 text-sm text-text-muted text-center">
                  Rien ne correspond à « {query} ».
                </li>
              ) : (
                visibles.map((e, i) => {
                  const Icone = e.icon;
                  return (
                    <li key={e.id} role="option" aria-selected={i === courant}>
                      <button
                        type="button"
                        onMouseEnter={() => setIndex(i)}
                        onClick={() => e.run()}
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-2.5 text-left border-l-[3px]",
                          i === courant
                            ? "bg-bg-card-hover border-accent"
                            : "border-transparent",
                        )}
                      >
                        <Icone
                          size={16}
                          className={e.kind === "creer" ? "text-accent-ink" : "text-text-muted"}
                        />
                        <span className="flex-1 text-sm text-text-primary">{e.label}</span>
                        <span className="text-[11px] text-text-muted">{e.hint}</span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>

            <p className="hidden sm:block px-4 py-2 border-t border-border text-[11px] text-text-muted">
              ↑ ↓ pour choisir · Entrée pour agir · tapez pour chercher une page
            </p>
        </Dialog>
      )}
    </>
  );
}
